/**
 * Faixa de identidade da empresa: logo, nome, segmento, saude dos funis e o
 * TOTAL do periodo, com o liquido em destaque.
 *
 * Decisoes nao obvias:
 *  1. Nao usa o `HeroBanner` do kit porque ele so aceita um `IconName` a
 *     esquerda e aqui a identidade e o `CompanyLogo` da empresa. A faixa
 *     repete as mesmas classes (contorno fino, gradiente `bg-hero` a 7%) e o
 *     mesmo `h1#titulo-da-pagina`. O `h1` e o NOME DA EMPRESA: esta e a
 *     pagina inicial do CRM dela, e o dono pediu logo e nome em cima.
 *  2. O numero grande e o LIQUIDO, nao o bruto: e o que fica com a empresa e
 *     e o que ela procura primeiro. Bruto e comissao ficam ao lado, e uma
 *     frase explica a comissao em linguagem de negocio (quanto, sobre o que,
 *     quando nao existe). "Gasto" e "custo" nao aparecem.
 *  3. "O que teria ficado na mesa" e o bruto recuperado: esses pagamentos
 *     so existem porque o motor agiu depois do abandono ou do vencimento.
 *     Ao lado, "ainda parado" e o que ainda da para recuperar -- o proximo
 *     salto, nao uma perda.
 *  4. A comparacao com o periodo anterior fica em cada medida (glifo + texto)
 *     e a base da comparacao e escrita uma vez, no rodape. Em "desde o
 *     inicio" nao ha anterior, e a faixa diz isso em vez de esconder a linha.
 */

import { ErrorPanel, RefreshingDot } from '../../../components/PageState';
import { Badge, CompanyLogo, Footnote, Icon } from '../../../components/ui';
import type { AsyncState } from '../../../lib/api';
import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../../lib/format';
import type { CompanyStatus, CompanySummary } from '../../../types';
import type { Diagnostico } from '../lib/saude';
import { descreverJanela } from '../lib/tempo';
import type { FunnelsSummary } from '../types';
import { Delta } from './Delta';
import { GradeMetricas, GradeMetricasEsqueleto } from './GradeMetricas';

const STATUS_FORA_DO_AR: Readonly<Record<Exclude<CompanyStatus, 'ativa'>, { readonly label: string; readonly tone: 'warning' | 'critical' }>> = {
  onboarding: { label: 'Em implantação', tone: 'warning' },
  pausada: { label: 'Pausada', tone: 'critical' },
};

export function FaixaEmpresa({
  company,
  periodo,
  estado,
  onRetry,
  atualizando,
  diagnosticos,
}: {
  readonly company: CompanySummary;
  /** Texto da janela do cabecalho, ex.: "Últimos 30 dias". */
  readonly periodo: string;
  readonly estado: AsyncState<FunnelsSummary>;
  readonly onRetry: () => void;
  readonly atualizando: boolean;
  /** Um por funil habilitado; vazio enquanto o resumo carrega. */
  readonly diagnosticos: readonly Diagnostico[];
}): JSX.Element {
  const funis = company.funnels.length;
  const status = company.status;
  const pedemAtencao = diagnosticos.filter((d) => d.saude === 'atencao' || d.saude === 'critico' || d.saude === 'pausado');

  return (
    <header className="relative overflow-hidden rounded-2xl border border-line bg-surface px-5 py-5 shadow-soft">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-hero opacity-[0.07]" />
      <div className="relative flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <CompanyLogo company={company} size="xl" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">Visão geral · {periodo}</p>
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                <h1 id="titulo-da-pagina" className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">
                  {company.name}
                </h1>
                <Badge tone="neutral" icon="funil">
                  {funis} {funis === 1 ? 'funil' : 'funis'}
                </Badge>
                {diagnosticos.length === 0 ? null : pedemAtencao.length === 0 ? (
                  <Badge tone="good" dot>
                    Funis saudáveis
                  </Badge>
                ) : (
                  <Badge tone={pedemAtencao.some((d) => d.saude === 'critico' || d.saude === 'pausado') ? 'critical' : 'warning'} dot>
                    {pedemAtencao.length === 1 ? '1 funil pede atenção' : `${pedemAtencao.length} funis pedem atenção`}
                  </Badge>
                )}
                {status === 'ativa' ? null : (
                  <Badge tone={STATUS_FORA_DO_AR[status].tone} dot>
                    {STATUS_FORA_DO_AR[status].label}
                  </Badge>
                )}
              </div>
              <p className="mt-0.5 text-sm text-ink-muted">{company.segment === '' ? 'Empresa em foco neste painel' : company.segment}</p>
            </div>
          </div>
          <RefreshingDot active={atualizando} />
        </div>

        <div className="border-t border-line/70 pt-4">
          {estado.status === 'loading' ? (
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,2fr)]">
              <div aria-hidden="true" className="flex animate-pulse flex-col gap-2">
                <div className="h-2.5 w-24 rounded bg-ink/10" />
                <div className="h-9 w-48 rounded bg-ink/10" />
                <div className="h-2.5 w-40 rounded bg-ink/10" />
              </div>
              <GradeMetricasEsqueleto quantidade={5} />
            </div>
          ) : null}
          {estado.status === 'error' ? <ErrorPanel title="Não foi possível carregar o total do período" error={estado.error} onRetry={onRetry} /> : null}
          {estado.status === 'success' ? <Total resumo={estado.data} /> : null}
        </div>
      </div>
    </header>
  );
}

function Total({ resumo }: { readonly resumo: FunnelsSummary }): JSX.Element {
  const total = resumo.total;
  const anterior = resumo.previousTotal;
  const semVenda = total.recoveredCount === 0;
  const parado = resumo.funnels.reduce((acc, f) => acc + f.openAmountCents, 0);
  const abertos = resumo.funnels.reduce((acc, f) => acc + f.openCount, 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,2fr)]">
        <div className="flex min-w-0 flex-col gap-1 lg:border-r lg:border-line/70 lg:pr-5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
            Líquido para você<span className="sr-only"> no período</span>
          </p>
          <p className={`truncate text-3xl font-semibold tracking-tight tabular-nums sm:text-4xl ${semVenda ? 'text-ink-muted' : 'text-status-good'}`}>
            {formatBRLFromCents(total.netAmountCents)}
          </p>
          {anterior === null ? (
            <p className="text-xs text-ink-subtle">Sem período anterior para comparar nesta janela.</p>
          ) : (
            <Delta atual={total.netAmountCents} anterior={anterior.netAmountCents} tipo="dinheiro" className="text-sm" />
          )}
          <p className="mt-2 text-sm text-ink-muted">
            {semVenda ? (
              'Ainda não houve venda recuperada neste período; sem venda, não há comissão.'
            ) : (
              <>
                De <strong className="font-semibold text-ink">{formatBRLFromCents(total.recoveredAmountCents)}</strong> recuperados,{' '}
                <strong className="font-semibold text-ink">{formatBRLFromCents(total.commissionCents)}</strong> ({formatPercentFromBps(resumo.feeBps)}) são a comissão do
                Converte Fácil: cobrada só sobre venda paga em até 7 dias depois de um contato, creditada ao último contato. O resto é seu.
              </>
            )}
          </p>
        </div>
        <GradeMetricas metricas={total} anterior={anterior} feeBps={resumo.feeBps} medidas={['bruto', 'comissao', 'vendas', 'ticket', 'taxa']} />
      </div>

      <div className="flex flex-col gap-2 border-t border-line/70 pt-3 sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-6">
        <p className="flex items-start gap-2 text-xs text-ink-muted">
          <Icon name="moeda" size="xs" className="mt-0.5 shrink-0 text-accent" />
          <span>
            {semVenda ? (
              <>Nada recuperado ainda neste período.</>
            ) : (
              <>
                <strong className="font-semibold text-ink">{formatBRLFromCents(total.recoveredAmountCents)}</strong> teriam ficado na mesa: eram checkouts abandonados e
                assinaturas vencidas antes de o motor agir.
              </>
            )}
            {parado > 0 ? (
              <>
                {' '}
                Ainda há <strong className="font-semibold text-ink">{formatBRLFromCents(parado)}</strong> parados em {formatCount(abertos)} {abertos === 1 ? 'lead' : 'leads'} dentro da
                janela.
              </>
            ) : null}
          </span>
        </p>
        <Footnote>
          A comissão existe porque recuperamos quem já tinha saído: nenhum toque acontece antes do abandono ou do vencimento.
          {resumo.previousPeriod === null ? null : <> Comparação com {descreverJanela(resumo.previousPeriod.start, resumo.previousPeriod.end)}, o período anterior de mesmo tamanho.</>}
        </Footnote>
      </div>
    </div>
  );
}
